import { useState, useEffect } from "react";
import axios from "axios";
import {
  Megaphone, Plus, Send, Trash2, Loader2, RefreshCw,
  Calendar, Clock, Users, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Jobs: "bg-blue-100 text-blue-700 border-blue-200",
  Bursary: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Alert: "bg-red-100 text-red-700 border-red-200",
  Incident: "bg-amber-100 text-amber-700 border-amber-200",
  Events: "bg-purple-100 text-purple-700 border-purple-200",
  "Lost & Found": "bg-orange-100 text-orange-700 border-orange-200",
  Acting: "bg-cyan-100 text-cyan-700 border-cyan-200",
  Death: "bg-slate-100 text-app border-app",
  General: "bg-violet-100 text-violet-700 border-violet-200",
  Other: "bg-slate-100 text-app border-app",
};

export default function Broadcast() {
  const auth = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [form, setForm] = useState({
    title: "",
    category: "General",
    body: "",
    duration_days: 7,
    closing_date: "",
    send_to_chat: false,
  });

  const fetchBroadcasts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      const res = await axios.get(`${API}/broadcast?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBroadcasts(res.data || []);
    } catch (err) {
      toast.error("Failed to load broadcasts");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBroadcasts();
  }, [selectedCategory]);

  const resetForm = () => {
    setForm({ title: "", category: "General", body: "", duration_days: 7, closing_date: "", send_to_chat: false });
    setEditId(null);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.body) {
      toast.error("Title and body are required");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...form,
        closing_date: form.closing_date || null,
      };
      await axios.post(`${API}/broadcast`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Broadcast created!");
      setFormOpen(false);
      resetForm();
      fetchBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create broadcast");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this broadcast?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/broadcast/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Broadcast deleted");
      fetchBroadcasts();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleSendToChat = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/broadcast/${id}/send-chat`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Sent to all users!");
      fetchBroadcasts();
    } catch (err) {
      toast.error("Failed to send");
    }
  };

  const getCategoryStyle = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.General;

  const getDaysLeft = (expiresAt) => {
    if (!expiresAt) return 0;
    const diff = new Date(expiresAt) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-app flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-violet-500" />
            Broadcast
          </h1>
          <p className="text-app-secondary mt-1">Create and manage organization-wide announcements</p>
        </div>
        <Button onClick={openCreate} className="bg-violet-500 hover:bg-violet-600">
          <Plus className="w-4 h-4 mr-2" /> New Broadcast
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "Jobs", "Bursary", "Alert", "Incident", "Events", "Lost & Found", "Acting", "Death", "General"].map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              selectedCategory === cat
                ? "bg-violet-500 text-white border-violet-500"
                : "bg-app-card text-app border-app hover:border-violet-300"
            }`}
          >
            {cat === "all" ? "All" : cat}
          </button>
        ))}
        <Button variant="outline" size="sm" onClick={fetchBroadcasts} className="ml-auto">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Broadcasts List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      ) : broadcasts.length === 0 ? (
        <div className="text-center py-20 text-app-secondary bg-app-card rounded-xl border border-app">
          <Megaphone className="w-12 h-12 mx-auto mb-3 text-app-secondary" />
          <p className="text-lg">No broadcasts yet</p>
          <p className="text-sm mt-1">Create your first announcement</p>
        </div>
      ) : (
        <div className="space-y-4">
          {broadcasts.map((b) => {
            const daysLeft = getDaysLeft(b.expires_at);
            return (
              <div
                key={b.id}
                className="bg-app-card rounded-xl border border-app p-6 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-0.5 rounded-full text-xs font-medium border ${getCategoryStyle(b.category)}`}>
                        {b.category}
                      </span>
                      {daysLeft <= 2 && (
                        <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          <Clock className="w-3 h-3" /> {daysLeft}d left
                        </span>
                      )}
                    </div>
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-app">{b.title}</h3>
                    <p className="text-sm text-app mt-2 whitespace-pre-wrap line-clamp-3">{b.body}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-app-secondary">
                      <span>By {b.created_by_name}</span>
                      <span>{b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}</span>
                      {b.closing_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Closes: {new Date(b.closing_date).toLocaleDateString()}
                        </span>
                      )}
                      {b.sent_to_chat && (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <Users className="w-3 h-3" /> Sent to all
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSendToChat(b.id)}
                      title="Send to all chat users"
                      disabled={b.sent_to_chat}
                    >
                      <Send className={`w-4 h-4 ${b.sent_to_chat ? "text-emerald-400" : "text-app-secondary"}`} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(b.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => { if (!o) { setFormOpen(false); resetForm(); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-violet-500" />
              New Broadcast
            </DialogTitle>
            <DialogDescription>Create an announcement for the entire organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-app mb-1">Category *</label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Jobs", "Bursary", "Alert", "Incident", "Events", "Lost & Found", "Acting", "Death", "General", "Other"].map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-app mb-1">Title *</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Announcement title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-app mb-1">Message *</label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Write your announcement here..."
                rows={6}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-app mb-1">Duration (days)</label>
                <Input
                  type="number"
                  value={form.duration_days}
                  onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 7 })}
                  min={1}
                  max={365}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-app mb-1">Closing Date (optional)</label>
                <Input
                  type="date"
                  value={form.closing_date}
                  onChange={(e) => setForm({ ...form, closing_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendToChat"
                checked={form.send_to_chat}
                onChange={(e) => setForm({ ...form, send_to_chat: e.target.checked })}
                className="rounded border-app"
              />
              <label htmlFor="sendToChat" className="text-sm text-app">
                Also send notification to all chat users
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setFormOpen(false); resetForm(); }} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="bg-violet-500 hover:bg-violet-600 flex-1">
                <Megaphone className="w-4 h-4 mr-2" /> Publish Broadcast
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
